/* La fiche d'un client : qui il est, ce qu'il achète, à quels prix, et ce qui
   reste ouvert chez lui.
 *
 * Tout y est modifiable, et chaque modification laisse sa trace dans le fil —
 * c'est le même geste : on enregistre, on inscrit ce qui a changé. */

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAgents, useClient, useContactsClient, usePackaging, usePrixClient, useProduits,
  useReclamations, useUsines, useVentes,
} from "@/lib/donnees";
import { autorise, useNiveau, useSession } from "@/lib/session";
import { Empty, Kpi, Kpis, Onglets, Section, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { Identite } from "@/ui/Fiche";
import { Table, dansUnChamp } from "@/ui/Table";
import { dateFr, depuis, entier, euro, euroCourt, jours, pourcent } from "@/lib/format";
import { GRAVITES, etatDe } from "@/domain/dossiers";
import { etapeDe as etapePackaging, stocks, stocksDuClient } from "@/domain/packaging";
import { EditeurContacts, Responsables } from "@/features/contacts/EditeurContacts";
import { etapeDe } from "@/domain/ventes";
import { enVigueur, historique } from "@/domain/prix";
import { CONFIANCES, GROUPES_CLIENT, SEGMENTS, libelleCourt } from "@/domain/fiches";
import { Journal } from "@/features/journal/Journal";
import { enregistrerFiche } from "@/features/journal/actions";
import type { Client } from "@/types/metier";

type Onglet = "fiche" | "ventes" | "tarifs" | "usines" | "packaging" | "qualite" | "suivi";

export function FicheClient() {
  const { id } = useParams<{ id: string }>();
  const navigue = useNavigate();
  const niveau = useNiveau();
  const { profil } = useSession();
  const voitUsine = autorise(niveau, "agent");
  const modifiable = autorise(niveau, "agent");

  const { donnees: client, chargement, erreur, recharger } = useClient(id);
  const { donnees: ventes } = useVentes();
  const { donnees: usines } = useUsines();
  const { donnees: produits } = useProduits();
  const { donnees: agents } = useAgents();
  const { donnees: dossiers } = usePackaging();
  const { donnees: reclamations } = useReclamations();
  const { donnees: tarifs } = usePrixClient(true);
  const { donnees: contacts, recharger: rechargerContacts } = useContactsClient(id);
  const [onglet, setOnglet] = useState<Onglet>("fiche");

  const nomUsine = useMemo(() => new Map(usines.map((u) => [u.id, u.nom] as const)), [usines]);
  const refProduit = useMemo(
    () => new Map(produits.map((p) => [p.id, p] as const)),
    [produits],
  );

  const sesVentes = useMemo(() => ventes.filter((v) => v.client_id === id), [ventes, id]);
  const sesDossiers = useMemo(() => dossiers.filter((d) => d.client_id === id), [dossiers, id]);
  const sesRecl = useMemo(() => reclamations.filter((r) => r.client_id === id), [reclamations, id]);

  /* Le stock de packaging ne se lit pas dans une colonne : il se recalcule à
     partir de ce qui a été reçu et de ce que les commandes ont consommé. */
  const lignesVente = useMemo(
    () => ventes.flatMap((v) => (v.lignes ?? []).map((l) => ({ ...l, vente: v }))),
    [ventes],
  );
  const monStock = useMemo(
    () => (id ? stocksDuClient(stocks(dossiers, ventes, lignesVente), id) : []),
    [dossiers, ventes, lignesVente, id],
  );

  /* Ses tarifs : ce qui court aujourd'hui, et l'archive de ce qui a couru. */
  const sesTarifs = useMemo(() => tarifs.filter((t) => t.client_id === id), [tarifs, id]);
  const tarifsVifs = useMemo(() => enVigueur(sesTarifs), [sesTarifs]);
  const tarifsPasses = useMemo(
    () => historique(sesTarifs.filter((t) => t.fin_le !== null)),
    [sesTarifs],
  );

  const parUsine = useMemo(() => {
    const m = new Map<string, { id: string; nom: string; nb: number; montant: number; derniere: string }>();
    for (const v of sesVentes) {
      const e = m.get(v.usine_id) ?? {
        id: v.usine_id,
        nom: voitUsine ? (nomUsine.get(v.usine_id) ?? "—") : "Usine masquée",
        nb: 0, montant: 0, derniere: "",
      };
      e.nb += 1;
      e.montant = Math.round((e.montant + Number(v.montant)) * 100) / 100;
      if (v.date_document > e.derniere) e.derniere = v.date_document;
      m.set(v.usine_id, e);
    }
    return [...m.values()].sort((a, b) => b.montant - a.montant);
  }, [sesVentes, nomUsine, voitUsine]);

  /* Les groupes de champs dépendent de la liste des agents : ce `useMemo` doit
     rester au-dessus des retours anticipés, sinon l'ordre des hooks change
     d'un rendu à l'autre. */
  const groupes = useMemo(() => {
    const suivi = {
      titre: "Suivi commercial",
      note: "qui le suit, et à quels taux",
      champs: [
        { cle: "agent_id" as const, nom: "Agent référent", type: "select" as const,
          options: agents.map((a) => ({ v: a.id, n: a.nom })) },
        { cle: "commission_defaut" as const, nom: "Commission par défaut", type: "pct" as const,
          aide: "Reprise à la création d'un devis, sauf barème plus précis." },
        { cle: "part_agent_defaut" as const, nom: "Part agent par défaut", type: "pct" as const },
      ],
    };
    return [...GROUPES_CLIENT.slice(0, 4), suivi, ...GROUPES_CLIENT.slice(4)];
  }, [agents]);

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;
  if (chargement) return <div className="pad"><div className="chargement">Chargement…</div></div>;
  if (!client) {
    return (
      <div className="pad">
        <Empty titre="Client introuvable" detail="Choisissez un client dans la liste." />
      </div>
    );
  }

  const ca = sesVentes.reduce((s, v) => s + Number(v.montant), 0);
  const encours = sesVentes.filter((v) => v.etape !== "paye");
  const duCours = encours.reduce((s, v) => s + Number(v.montant), 0);
  const derniere = sesVentes.reduce((d, v) => (v.date_document > d ? v.date_document : d), "");
  const limite = client.limite_encours == null ? null : Number(client.limite_encours);
  const depasse = limite != null && duCours > limite;

  /* L'agent qui le suit : celui qui est désigné sur la fiche, sinon celui qui
     vend le plus — parce qu'en pratique c'est lui qu'on appelle. */
  const agentFiche = client.agent_id ? agents.find((a) => a.id === client.agent_id) : undefined;

  const enregistrer = async (champs: Partial<Client>) => {
    if (!profil) throw new Error("Profil inconnu : reconnectez-vous.");
    await enregistrerFiche<Client>(
      { sujet: "clients", sujetId: client.id, auteur: profil.id },
      "clients",
      client as unknown as Record<string, unknown>,
      champs as Record<string, unknown>,
    );
    recharger();
  };

  const onglets: { cle: Onglet; nom: string; n?: number }[] = [
    { cle: "fiche", nom: "Fiche" },
    { cle: "ventes", nom: "Ventes", n: sesVentes.length },
    { cle: "tarifs", nom: "Tarifs", n: tarifsVifs.length },
    { cle: "usines", nom: voitUsine ? "Usines" : "Origines", n: parUsine.length },
    { cle: "packaging", nom: "Packaging", n: sesDossiers.length },
    { cle: "qualite", nom: "Qualité", n: sesRecl.length },
    { cle: "suivi", nom: "Suivi" },
  ];

  return (
    <div className="pad">
      <div className="fiche-tete">
        <div className="fiche-t">
          <div className="section-t">{client.nom}</div>
          <div className="section-n">
            {[
              client.segment ? libelleCourt(SEGMENTS, client.segment) : null,
              client.ville,
              client.pays,
            ].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <div className="fiche-a fiche-a-centre">
          {client.type === "client" ? <Tag ton="ok" dot>client</Tag> : <Tag ton="warn" dot>prospect</Tag>}
          {client.confiance ? (
            <Tag ton={client.confiance === "bonne" ? "ok" : client.confiance === "douteuse" ? "crit" : "warn"}>
              {libelleCourt(CONFIANCES, client.confiance)}
            </Tag>
          ) : null}
          {agentFiche ? <Tag>{agentFiche.nom}</Tag> : null}
          <button type="button" className="btn" onClick={() => navigue("/clients")}>
            <Icon n="retour" cls="" />
            Tous les clients
          </button>
        </div>
      </div>

      <div className="fiche-parcours">
        <Onglets items={onglets} actif={onglet} onChange={setOnglet} />
      </div>

      {onglet === "fiche" ? (
        <>
          <Kpis>
            <Kpi k="Chiffre d'affaires" v={euroCourt(ca)} n={`${sesVentes.length} documents`} />
            <Kpi
              k="En cours"
              v={euroCourt(duCours)}
              n={
                limite == null
                  ? `${encours.length} non soldés`
                  : `${encours.length} non soldés · limite ${euroCourt(limite)}`
              }
            />
            <Kpi
              k="Dernière vente"
              v={derniere ? dateFr(derniere) : "—"}
              n={derniere ? jours(depuis(derniere)) : "aucune vente"}
            />
            <Kpi k="Réclamations" v={entier(sesRecl.length)} n={`${tarifsVifs.length} tarifs posés`} />
          </Kpis>

          {depasse ? (
            <div className="banner">
              <Icon n="info" cls="" />
              <div>
                L'encours non soldé dépasse la limite accordée :{" "}
                <b className="mono">{euro(duCours)}</b> contre <b className="mono">{euro(limite)}</b>.
              </div>
            </div>
          ) : null}

          <Identite
            valeur={client}
            groupes={groupes}
            modifiable={modifiable}
            enregistrer={enregistrer}
          />

          <div className="grid2">
            <div>
              <Section titre="Responsables" note="qui reçoit quoi">
                <div className="card card-pad">
                  <Responsables liste={contacts} />
                </div>
              </Section>
            </div>
            <div>
              <EditeurContacts
                table="contacts_client"
                colonne="client_id"
                parent={client.id}
                liste={contacts}
                modifiable={modifiable}
                recharger={rechargerContacts}
              />
            </div>
          </div>
        </>
      ) : null}

      {onglet === "ventes" ? (
        <Section titre="Ventes" note={`${sesVentes.length} documents`}>
          <Table
            lignes={sesVentes}
            cle={(v) => v.id}
            chercher="Document"
            onClic={(v, ev) => {
              if (!dansUnChamp(ev)) navigue(`/ventes/${v.id}`);
            }}
            colonnes={[
              { cle: "reference", titre: "Document", type: "ref", filtre: "texte",
                sous: (v) => etapeDe(v.etape).court },
              voitUsine && { cle: "usine_id", titre: "Usine", filtre: "select", mobile: false,
                texte: (v) => nomUsine.get(v.usine_id) ?? "—" },
              { cle: "montant", titre: "Montant HT", court: "Montant", type: "euro", total: true,
                nombre: (v) => Number(v.montant) },
              { cle: "etape", titre: "Étape", filtre: "select",
                texte: (v) => etapeDe(v.etape).nom,
                rendu: (v) => <Tag ton={etapeDe(v.etape).ton} dot>{etapeDe(v.etape).court}</Tag> },
              { cle: "date_document", titre: "Date", type: "date", mobile: false,
                rendu: (v) => <span className="mono">{dateFr(v.date_document)}</span> },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "tarifs" ? (
        <>
          <Section
            titre="Tarifs en vigueur"
            note="ce qu'il paie aujourd'hui, référence par référence"
          >
            {tarifsVifs.length ? (
              <Table
                lignes={tarifsVifs}
                cle={(t) => t.id}
                chercher="Référence"
                onClic={(t, ev) => {
                  if (!dansUnChamp(ev)) navigue(`/catalogue/${t.produit_id}`);
                }}
                colonnes={[
                  { cle: "produit_id", titre: "Référence", type: "ref", filtre: "texte",
                    texte: (t) => refProduit.get(t.produit_id)?.ref ?? "—",
                    sous: (t) => refProduit.get(t.produit_id)?.nom ?? "" },
                  { cle: "nom", titre: "Désignation", classe: "name", mobile: false,
                    texte: (t) => refProduit.get(t.produit_id)?.nom ?? "—" },
                  { cle: "prix", titre: "Prix convenu", court: "Prix", type: "euro",
                    nombre: (t) => Number(t.prix) },
                  { cle: "debut_le", titre: "Depuis", type: "date", mobile: false },
                  { cle: "motif", titre: "Motif", mobile: false, texte: (t) => t.motif ?? "—" },
                ]}
              />
            ) : (
              <Empty
                titre="Aucun tarif propre"
                detail="Ce client paie le prix du catalogue. Un devis accepté au-dessus du plancher pose son tarif."
              />
            )}
          </Section>

          {tarifsPasses.length ? (
            <Section titre="Archives" note={`${tarifsPasses.length} périodes closes`}>
              <Table
                lignes={tarifsPasses}
                cle={(t) => t.id}
                colonnes={[
                  { cle: "produit_id", titre: "Référence", type: "ref",
                    texte: (t) => refProduit.get(t.produit_id)?.ref ?? "—" },
                  { cle: "prix", titre: "Prix", type: "euro", nombre: (t) => Number(t.prix) },
                  { cle: "debut_le", titre: "Du", type: "date" },
                  { cle: "fin_le", titre: "Au", type: "date", mobile: false },
                  { cle: "motif", titre: "Motif", mobile: false, texte: (t) => t.motif ?? "—" },
                ]}
              />
            </Section>
          ) : null}
        </>
      ) : null}

      {onglet === "usines" ? (
        <Section
          titre={voitUsine ? "Par usine" : "Par origine"}
          note="ce qu'il achète, et à qui"
        >
          <Table
            lignes={parUsine}
            cle={(u) => u.id}
            colonnes={[
              { cle: "nom", titre: voitUsine ? "Usine" : "Origine",
                sous: (u) => `${u.nb} ${u.nb > 1 ? "ventes" : "vente"}` },
              { cle: "nb", titre: "Ventes", type: "num", total: true, mobile: false },
              { cle: "montant", titre: "Chiffre d'affaires", court: "CA", type: "euro", total: true },
              { cle: "derniere", titre: "Dernière", type: "date", mobile: false },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "packaging" ? (
        <>
          {/* Ce que le client a en stock passe avant les dossiers : c'est ce
              qu'on regarde avant de prendre une commande. */}
          <Section
            titre="Packagings en stock"
            note="ce qui reste, référence par référence — chaque commande l'entame"
          >
            {monStock.length ? (
              <Table
                lignes={monStock}
                cle={(s) => s.produit_id}
                onClic={(s, ev) => {
                  if (!dansUnChamp(ev)) navigue(`/catalogue/${s.produit_id}`);
                }}
                colonnes={[
                  { cle: "produit_id", titre: "Référence", type: "ref", filtre: "texte",
                    texte: (s) => refProduit.get(s.produit_id)?.ref ?? "—",
                    sous: (s) => refProduit.get(s.produit_id)?.nom ?? "" },
                  { cle: "nom", titre: "Désignation", classe: "name", mobile: false,
                    texte: (s) => refProduit.get(s.produit_id)?.nom ?? "—" },
                  voitUsine && { cle: "usine_id", titre: "Fournisseur", mobile: false,
                    texte: (s) => (s.usine_id ? (nomUsine.get(s.usine_id) ?? "—") : "—") },
                  { cle: "recu", titre: "Reçu", type: "num", total: true, mobile: false },
                  { cle: "restant", titre: "Restant", type: "num", total: true,
                    rendu: (s) => (
                      <span className={s.recu > 0 && s.restant / s.recu < 0.15
                        ? "mono marge-ko" : "mono"}>
                        {entier(s.restant)}
                      </span>
                    ) },
                ]}
              />
            ) : (
              <Empty
                titre="Aucun packaging en stock"
                detail="Un dossier arrivé à la réception alimente ce stock, et les commandes l'entament."
              />
            )}
          </Section>

          <Section titre="Dossiers de packaging" note={`${sesDossiers.length} dossiers`}>
            <Table
              lignes={sesDossiers}
              cle={(d) => d.id}
              onClic={(d, ev) => {
                if (!dansUnChamp(ev)) navigue(`/packaging/${d.id}`);
              }}
              colonnes={[
                { cle: "reference", titre: "Dossier", type: "ref", filtre: "texte",
                  sous: (d) => d.intitule },
                { cle: "intitule", titre: "Intitulé", classe: "name", mobile: false },
                { cle: "produit_id", titre: "Référence", mobile: false,
                  texte: (d) => (d.produit_id ? (refProduit.get(d.produit_id)?.ref ?? "—") : "—") },
                {
                  cle: "etape", titre: "Étape", filtre: "select",
                  texte: (d) => etapePackaging(d.etape).nom,
                  rendu: (d) => {
                    const e = etapePackaging(d.etape);
                    return <Tag ton={e.ton} dot>{e.court}</Tag>;
                  },
                },
                { cle: "echeance", titre: "Échéance", type: "date", mobile: false },
              ]}
            />
          </Section>
        </>
      ) : null}

      {onglet === "qualite" ? (
        <Section titre="Réclamations" note={`${sesRecl.length} dossiers`}>
          <Table
            lignes={sesRecl}
            cle={(r) => r.id}
            colonnes={[
              { cle: "reference", titre: "Réclamation", court: "Récl.", type: "ref",
                filtre: "texte", sous: (r) => r.sujet },
              { cle: "sujet", titre: "Sujet", classe: "name", mobile: false },
              {
                cle: "gravite", titre: "Gravité", filtre: "select",
                texte: (r) => etatDe(GRAVITES, r.gravite).nom,
                rendu: (r) => {
                  const g = etatDe(GRAVITES, r.gravite);
                  return <Tag ton={g.ton} dot>{g.nom}</Tag>;
                },
              },
              { cle: "impact", titre: "Montant en jeu", court: "En jeu", type: "euro", total: true,
                nombre: (r) => Number(r.impact ?? 0) },
              { cle: "ouverte_le", titre: "Ouverte", type: "date", mobile: false },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "suivi" ? <Journal sujet="clients" sujetId={client.id} /> : null}

      <div className="vide-ligne">
        Total facturé à ce client : <b className="mono">{euro(ca)}</b>
        {client.commission_defaut != null ? (
          <> · commission par défaut <b className="mono">{pourcent(Number(client.commission_defaut))}</b></>
        ) : null}
      </div>
    </div>
  );
}
